import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const adminApi = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Handle session expiry — auth is carried by the httpOnly cookie
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_user');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

export const adminService = {
  // Dashboard
  async getDashboardStats() {
    const response = await adminApi.get('/admin/dashboard');
    return response.data;
  },

  async getDashboardKPIs(months = 6) {
    const response = await adminApi.get(`/admin/dashboard/kpis?months=${months}`);
    return response.data;
  },

  // -------- App Settings --------
  async getOrderingDeadlines() {
    const response = await adminApi.get('/admin/settings/ordering-deadlines');
    return response.data;
  },

  async updateOrderingDeadlines(payload) {
    const response = await adminApi.put('/admin/settings/ordering-deadlines', payload);
    return response.data;
  },

  async getTimezoneSettings() {
    const response = await adminApi.get('/admin/settings/timezone');
    return response.data;
  },

  async updateTimezoneSettings(payload) {
    const response = await adminApi.put('/admin/settings/timezone', payload);
    return response.data;
  },

  async getOverdueReminderSettings() {
    const response = await adminApi.get('/admin/settings/overdue-reminders');
    return response.data;
  },

  async updateOverdueReminderSettings(payload) {
    const response = await adminApi.put('/admin/settings/overdue-reminders', payload);
    return response.data;
  },

  // -------- Menu Calendar --------
  async getMenuDays(start, end) {
    const response = await adminApi.get(`/admin/menu-days?start=${start}&end=${end}`);
    return response.data;
  },

  async getMenuDay(date) {
    const response = await adminApi.get(`/admin/menu-days/${date}`);
    return response.data;
  },

  async createMenuDay(payload) {
    const response = await adminApi.post('/admin/menu-days', payload);
    return response.data;
  },

  async updateMenuDay(date, payload) {
    const response = await adminApi.put(`/admin/menu-days/${date}`, payload);
    return response.data;
  },

  async toggleMenuPublish(date, published) {
    const response = await adminApi.post(`/admin/menu-days/${date}/publish`, { published });
    return response.data;
  },

  async deleteMenuDay(date) {
    const response = await adminApi.delete(`/admin/menu-days/${date}`);
    return response.data;
  },

  async notifyMenuDay(date, notify = true) {
    const response = await adminApi.post(`/admin/menu-days/${date}/notify`, { notify });
    return response.data;
  },

  async extendMenuDay(date, payload) {
    const response = await adminApi.post(`/admin/menu-days/${date}/extend`, payload);
    return response.data;
  },

  // --- Date-range menus (batches) + publish-with-notify ---
  async previewMenuRange(payload) {
    const response = await adminApi.post('/admin/menu-days/range/preview', payload);
    return response.data;
  },

  async applyMenuRange(payload) {
    const response = await adminApi.post('/admin/menu-days/range/apply', payload);
    return response.data;
  },

  async getMenuBatches() {
    const response = await adminApi.get('/admin/menu-days/batches');
    return response.data;
  },

  async unpublishMenuBatch(batchId) {
    const response = await adminApi.post(`/admin/menu-days/batch/${batchId}/unpublish`);
    return response.data;
  },

  async notifyMenuBatch(batchId, notify = true) {
    const response = await adminApi.post(`/admin/menu-days/batch/${batchId}/notify`, { notify });
    return response.data;
  },

  async previewMenuBatchEdit(batchId, payload) {
    const response = await adminApi.post(`/admin/menu-days/batch/${batchId}/edit-preview`, payload);
    return response.data;
  },

  async previewMenuOrderImpact(payload) {
    const response = await adminApi.post('/admin/menu-days/order-impact', payload);
    return response.data;
  },

  async updateMenuBatch(batchId, payload) {
    const response = await adminApi.put(`/admin/menu-days/batch/${batchId}`, payload);
    return response.data;
  },

  // ---- Order Insights (sales history) ----
  async syncSalesHistory() {
    const response = await adminApi.post('/admin/cron/sales-history-sync');
    return response.data;
  },

  async downloadSalesHistoryTemplate() {
    const response = await adminApi.get('/admin/sales-history/import/template', { responseType: 'blob' });
    return response.data;
  },

  async previewSalesHistoryImport(rows) {
    const response = await adminApi.post('/admin/sales-history/import/preview', { rows });
    return response.data;
  },

  async applySalesHistoryImport(payload) {
    const response = await adminApi.post('/admin/sales-history/import/apply', payload);
    return response.data;
  },

  async listSalesHistory(params = {}) {
    const response = await adminApi.get('/admin/sales-history', { params });
    return response.data;
  },

  async deleteSalesHistoryBatch(batchId) {
    const response = await adminApi.delete(`/admin/sales-history/import/${batchId}`);
    return response.data;
  },

  async getOrderInsights(orderId) {
    const response = await adminApi.get(`/admin/orders/${orderId}/insights`);
    return response.data;
  },

  async getClientInsights(clientAccountId, deliveryDate) {
    const response = await adminApi.get('/admin/order-insights', {
      params: { client_account_id: clientAccountId, delivery_date: deliveryDate },
    });
    return response.data;
  },

  async getOrderDeliveryProof(orderId) {
    const response = await adminApi.get(`/admin/orders/${orderId}/delivery-proof`);
    return response.data;
  },

  // -------- Over-ordering controls --------
  async getOverOrderSettings() {
    const response = await adminApi.get('/admin/over-order/settings');
    return response.data;
  },

  async updateOverOrderSettings(payload) {
    const response = await adminApi.put('/admin/over-order/settings', payload);
    return response.data;
  },

  async getPendingApprovals() {
    const response = await adminApi.get('/admin/over-order/pending-approvals');
    return response.data;
  },

  async getOrderApproval(orderId) {
    const response = await adminApi.get(`/admin/over-order/orders/${orderId}`);
    return response.data;
  },

  async approveOverOrder(orderId, payload) {
    const response = await adminApi.post(`/admin/over-order/orders/${orderId}/approve`, payload);
    return response.data;
  },

  async getClientGating(clientId) {
    const response = await adminApi.get(`/admin/over-order/clients/${clientId}/gating`);
    return response.data;
  },

  async setClientGating(clientId, payload) {
    const response = await adminApi.put(`/admin/over-order/clients/${clientId}/gating`, payload);
    return response.data;
  },

  async setClientAllowance(clientId, allowancePct) {
    const response = await adminApi.put(`/admin/over-order/clients/${clientId}/allowance`, {
      allowance_pct: allowancePct,
    });
    return response.data;
  },

  async getPersistentFailures(clientId, params = {}) {
    const response = await adminApi.get(
      `/admin/over-order/clients/${clientId}/persistent-failures`, { params },
    );
    return response.data;
  },

  async applyStandingReductions(clientId, reductions) {
    const response = await adminApi.post(
      `/admin/over-order/clients/${clientId}/apply-reductions`, { reductions },
    );
    return response.data;
  },

  async runOverOrderGating() {
    const response = await adminApi.post('/admin/cron/over-order-gating');
    return response.data;
  },

  async runOverOrderReleases() {
    const response = await adminApi.post('/admin/cron/over-order-releases');
    return response.data;
  },

  async sendOrderDeliveryProof(orderId, message) {
    const response = await adminApi.post(`/admin/orders/${orderId}/delivery-proof/send`, { message });
    return response.data;
  },

  // -------- Menu announcement recipients --------
  // Track A extras: bare-email contacts on a client account (admin on behalf).
  async getClientAnnouncementContacts(clientId) {
    const response = await adminApi.get(`/admin/clients/${clientId}/announcement-contacts`);
    return response.data;
  },

  async addClientAnnouncementContact(clientId, payload) {
    const response = await adminApi.post(`/admin/clients/${clientId}/announcement-contacts`, payload);
    return response.data;
  },

  async updateClientAnnouncementContact(clientId, contactId, payload) {
    const response = await adminApi.patch(
      `/admin/clients/${clientId}/announcement-contacts/${contactId}`, payload,
    );
    return response.data;
  },

  async deleteClientAnnouncementContact(clientId, contactId) {
    const response = await adminApi.delete(
      `/admin/clients/${clientId}/announcement-contacts/${contactId}`,
    );
    return response.data;
  },

  // Track B: marketing recipients (no pricing).
  async getMarketingRecipients() {
    const response = await adminApi.get('/admin/settings/menu-marketing-recipients');
    return response.data;
  },

  async addMarketingRecipient(payload) {
    const response = await adminApi.post('/admin/settings/menu-marketing-recipients', payload);
    return response.data;
  },

  async updateMarketingRecipient(recipientId, payload) {
    const response = await adminApi.patch(
      `/admin/settings/menu-marketing-recipients/${recipientId}`, payload,
    );
    return response.data;
  },

  async deleteMarketingRecipient(recipientId) {
    const response = await adminApi.delete(
      `/admin/settings/menu-marketing-recipients/${recipientId}`,
    );
    return response.data;
  },

  async testMarketingRecipient(recipientId) {
    const response = await adminApi.post(
      `/admin/settings/menu-marketing-recipients/${recipientId}/test`,
    );
    return response.data;
  },

  async getMenuRecipientsCount() {
    const response = await adminApi.get('/admin/menu-days/recipients-count');
    return response.data;
  },

  // -------- Admin Inventory --------
  async getInventory(filters = {}) {
    const params = new URLSearchParams();
    if (filters.category) params.append('category', filters.category);
    if (filters.low_stock_only) params.append('low_stock_only', 'true');
    const response = await adminApi.get(`/admin/inventory?${params}`);
    return response.data;
  },

  async adjustInventory(payload) {
    const response = await adminApi.post('/admin/inventory/adjust', payload);
    return response.data;
  },

  async setInventoryPar(payload) {
    const response = await adminApi.post('/admin/inventory/set-par', payload);
    return response.data;
  },

  async getInventoryCategories() {
    const response = await adminApi.get('/admin/inventory/categories');
    return response.data;
  },

  async createIngredient(payload) {
    const response = await adminApi.post('/admin/inventory', payload);
    return response.data;
  },

  async updateIngredient(id, payload) {
    const response = await adminApi.put(`/admin/inventory/${id}`, payload);
    return response.data;
  },

  async getInventoryNotificationSettings() {
    const response = await adminApi.get('/admin/settings/inventory-notifications');
    return response.data;
  },

  async updateInventoryNotificationSettings(payload) {
    const response = await adminApi.put('/admin/settings/inventory-notifications', payload);
    return response.data;
  },

  async triggerInventoryCountReminders() {
    const response = await adminApi.post('/admin/cron/inventory-count-reminders');
    return response.data;
  },

  async triggerInventoryChangeDigest() {
    const response = await adminApi.post('/admin/cron/inventory-change-digest');
    return response.data;
  },

  async triggerOverdueReminderCron() {
    const response = await adminApi.post('/admin/cron/overdue-invoice-reminders');
    return response.data;
  },

  async getClientEmailEvents(clientId, { days = 30, eventType = null, limit = 200 } = {}) {
    const params = new URLSearchParams({ days: String(days), limit: String(limit) });
    if (eventType) params.set('event_type', eventType);
    const response = await adminApi.get(`/admin/clients/${clientId}/email-events?${params.toString()}`);
    return response.data;
  },

  // -------- CSV Exports --------
  async getExportColumns(entity) {
    const response = await adminApi.get(`/admin/exports/columns/${entity}`);
    return response.data;
  },

  // -------- Pickup Discrepancy Reports --------
  async getPickupDiscrepancies(params = {}) {
    const response = await adminApi.get('/admin/reports/pickup-discrepancies', { params });
    return response.data;
  },

  async getPickupDiscrepanciesSummary(params = {}) {
    const response = await adminApi.get('/admin/reports/pickup-discrepancies/summary', { params });
    return response.data;
  },

  /**
   * Download a CSV file for the given entity. Triggers browser save-as via blob.
   * `params` is a plain object of query params honored by the backend route.
   */
  async downloadExport(entity, params = {}) {
    const response = await adminApi.get(`/admin/exports/${entity}.csv`, {
      params,
      responseType: 'blob',
    });
    const disposition = response.headers['content-disposition'] || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match ? match[1] : `${entity}.csv`;
    const url = window.URL.createObjectURL(
      new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
    );
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return filename;
  },

  // Products
  async getProducts(filters = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.category) params.append('category', filters.category);
    const response = await adminApi.get(`/admin/products?${params}`);
    return response.data;
  },

  async getProduct(id) {
    const response = await adminApi.get(`/admin/products/${id}`);
    return response.data;
  },

  async createProduct(productData) {
    const response = await adminApi.post('/admin/products', productData);
    return response.data;
  },

  async updateProduct(id, productData) {
    const response = await adminApi.put(`/admin/products/${id}`, productData);
    return response.data;
  },

  async toggleProductStatus(id) {
    const response = await adminApi.patch(`/admin/products/${id}/toggle-status`);
    return response.data;
  },

  async duplicateProduct(id) {
    const response = await adminApi.post(`/admin/products/${id}/duplicate`);
    return response.data;
  },

  async batchUpdateProducts(productIds, updates) {
    const response = await adminApi.post('/admin/products/batch-update', {
      product_ids: productIds,
      updates
    });
    return response.data;
  },

  async deleteProduct(id) {
    const response = await adminApi.delete(`/admin/products/${id}`);
    return response.data;
  },

  async batchDeleteProducts(productIds) {
    const response = await adminApi.post('/admin/products/batch-delete', {
      product_ids: productIds
    });
    return response.data;
  },

  // Orders
  async getRecurringTemplates(filters = {}) {
    const params = new URLSearchParams();
    if (filters.client_account_id) params.append('client_account_id', filters.client_account_id);
    if (filters.status) params.append('status', filters.status);
    if (filters.origin) params.append('origin', filters.origin);
    if (filters.q) params.append('q', filters.q);
    const qs = params.toString();
    const response = await adminApi.get(`/admin/recurring-orders${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  async getRecurringTemplate(id) {
    const response = await adminApi.get(`/admin/recurring-orders/${id}`);
    return response.data;
  },

  async createRecurringTemplate(payload) {
    const response = await adminApi.post('/admin/recurring-orders', payload);
    return response.data;
  },

  async updateRecurringTemplate(id, payload) {
    const response = await adminApi.put(`/admin/recurring-orders/${id}`, payload);
    return response.data;
  },

  async setRecurringTemplateStatus(id, status) {
    const response = await adminApi.post(`/admin/recurring-orders/${id}/status`, { status });
    return response.data;
  },

  async deleteRecurringTemplate(id, hard = false) {
    const response = await adminApi.delete(`/admin/recurring-orders/${id}${hard ? '?hard=true' : ''}`);
    return response.data;
  },

  async cancelRecurringTemplate(id) {
    const response = await adminApi.post(`/admin/recurring-orders/${id}/cancel`);
    return response.data;
  },

  async confirmRecurringOccurrence(occurrenceId, overrides = null) {
    const body = overrides !== null ? { overrides } : {};
    const response = await adminApi.post(
      `/admin/recurring-orders/occurrences/${occurrenceId}/confirm`, body,
    );
    return response.data;
  },

  async skipRecurringOccurrence(occurrenceId) {
    const response = await adminApi.post(
      `/admin/recurring-orders/occurrences/${occurrenceId}/skip`,
    );
    return response.data;
  },

  async editRecurringOccurrenceOverrides(occurrenceId, overrides) {
    const response = await adminApi.put(
      `/admin/recurring-orders/occurrences/${occurrenceId}/overrides`,
      { overrides },
    );
    return response.data;
  },

  async previewRecurringDates(payload) {
    const response = await adminApi.post('/admin/recurring-orders/preview-dates', payload);
    return response.data;
  },

  async getOrders(filters = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.client_id) params.append('client_id', filters.client_id);
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    const response = await adminApi.get(`/admin/orders?${params}`);
    return response.data;
  },

  async getOrder(id) {
    const response = await adminApi.get(`/admin/orders/${id}`);
    return response.data;
  },

  async updateOrderStatus(id, status) {
    const response = await adminApi.patch(`/admin/orders/${id}/status`, { status });
    return response.data;
  },

  async getProductsForOrder(clientId) {
    const params = clientId ? `?client_id=${clientId}` : '';
    const response = await adminApi.get(`/admin/orders/products-for-order${params}`);
    return response.data;
  },

  async adminCreateOrder(orderData) {
    const response = await adminApi.post('/admin/orders/create', orderData);
    return response.data;
  },

  async getOrderLocationChoices(orderId) {
    const response = await adminApi.get(`/admin/orders/${orderId}/location-choices`);
    return response.data;
  },

  async setOrderLocation(orderId, locationId, deliveryAddress) {
    const response = await adminApi.patch(`/admin/orders/${orderId}/location`, {
      location_id: locationId, delivery_address: deliveryAddress,
    });
    return response.data;
  },

  async getOrderLocationBacklog() {
    const response = await adminApi.get('/admin/orders/location-backlog');
    return response.data;
  },

  async applyOrderLocationBacklog(orderIds) {
    const response = await adminApi.post('/admin/orders/location-backlog/apply', {
      order_ids: orderIds,
    });
    return response.data;
  },

  async adminCreateCustomOrder(orderData) {
    const response = await adminApi.post('/admin/custom-orders', orderData);
    return response.data;
  },

  async getCustomOrder(orderId) {
    const response = await adminApi.get(`/admin/custom-orders/${orderId}`);
    return response.data;
  },

  async updateCustomOrder(orderId, orderData) {
    const response = await adminApi.put(`/admin/custom-orders/${orderId}`, orderData);
    return response.data;
  },

  // Clients
  async getClients() {
    const response = await adminApi.get('/admin/clients');
    return response.data;
  },

  async getClient(id) {
    const response = await adminApi.get(`/admin/clients/${id}`);
    return response.data;
  },

  async updateClientPreferences(clientId, preferences) {
    const response = await adminApi.put(`/admin/clients/${clientId}/preferences`, preferences);
    return response.data;
  },

  async addClientNote(clientId, content) {
    const response = await adminApi.post(`/admin/clients/${clientId}/notes`, { content });
    return response.data;
  },

  async deleteClientNote(clientId, noteId) {
    const response = await adminApi.delete(`/admin/clients/${clientId}/notes/${noteId}`);
    return response.data;
  },

  async addClientLocation(clientId, location) {
    const response = await adminApi.post(`/admin/clients/${clientId}/locations`, location);
    return response.data;
  },

  async updateClientLocation(clientId, locationId, location) {
    const response = await adminApi.put(`/admin/clients/${clientId}/locations/${locationId}`, location);
    return response.data;
  },

  async deleteClientLocation(clientId, locationId) {
    const response = await adminApi.delete(`/admin/clients/${clientId}/locations/${locationId}`);
    return response.data;
  },

  async getClientAuditLog(clientId, page = 1, perPage = 20) {
    const response = await adminApi.get(`/admin/clients/${clientId}/audit-log?page=${page}&per_page=${perPage}`);
    return response.data;
  },

  // Client account logins (multi-login)
  async getClientUsers(clientId) {
    const response = await adminApi.get(`/admin/clients/${clientId}/users`);
    return response.data;
  },

  async addClientUser(clientId, payload) {
    const response = await adminApi.post(`/admin/clients/${clientId}/users`, payload);
    return response.data;
  },

  async updateClientUserRole(clientId, membershipId, accountRole) {
    const response = await adminApi.put(`/admin/clients/${clientId}/users/${membershipId}`, { account_role: accountRole });
    return response.data;
  },

  async setClientUserStatus(clientId, membershipId, status) {
    const response = await adminApi.post(`/admin/clients/${clientId}/users/${membershipId}/status`, { status });
    return response.data;
  },

  async resendClientUserInvite(clientId, membershipId) {
    const response = await adminApi.post(`/admin/clients/${clientId}/users/${membershipId}/resend-invite`);
    return response.data;
  },

  async removeClientUser(clientId, membershipId) {
    const response = await adminApi.delete(`/admin/clients/${clientId}/users/${membershipId}`);
    return response.data;
  },

  // Orders - Edit & Batch
  async updateOrder(orderId, orderData) {
    const response = await adminApi.put(`/admin/orders/${orderId}`, orderData);
    return response.data;
  },

  // Advance invoicing (admin exception path)
  async issueAdvanceInvoice(orderId, reason) {
    const response = await adminApi.post(`/admin/orders/${orderId}/invoice-now`, { reason });
    return response.data;
  },

  async getOrderChangeRequests(status = 'pending') {
    const params = status ? { status } : {};
    const response = await adminApi.get('/admin/order-change-requests', { params });
    return response.data;
  },

  async approveOrderChangeRequest(requestId) {
    const response = await adminApi.post(`/admin/order-change-requests/${requestId}/approve`);
    return response.data;
  },

  async rejectOrderChangeRequest(requestId, reason) {
    const response = await adminApi.post(`/admin/order-change-requests/${requestId}/reject`, { reason });
    return response.data;
  },

  async getRevenueReport(params = {}) {
    const response = await adminApi.get('/admin/reports/revenue', { params });
    return response.data;
  },

  // -------- Client Performance analytics --------
  async getClientPerformanceFilters() {
    const response = await adminApi.get('/admin/reports/client-performance/filters');
    return response.data;
  },

  async getClientPerformanceSummary(params = {}) {
    const response = await adminApi.get('/admin/reports/client-performance/summary', { params });
    return response.data;
  },

  async getClientPerformanceTrend(params = {}) {
    const response = await adminApi.get('/admin/reports/client-performance/trend', { params });
    return response.data;
  },

  async getClientPerformanceReturnsByProduct(params = {}) {
    const response = await adminApi.get('/admin/reports/client-performance/returns-by-product', { params });
    return response.data;
  },

  async getClientPerformanceSeasonality(params = {}) {
    const response = await adminApi.get('/admin/reports/client-performance/seasonality', { params });
    return response.data;
  },

  async getClientPerformanceDrilldown(clientId, params = {}) {
    const response = await adminApi.get(
      `/admin/reports/client-performance/clients/${clientId}/drilldown`, { params },
    );
    return response.data;
  },

  async getClientScorecard(clientId, params = {}) {
    const response = await adminApi.get(
      `/admin/reports/client-performance/clients/${clientId}/scorecard`, { params },
    );
    return response.data;
  },

  async getAtRiskClients(limit = 3) {
    const response = await adminApi.get('/admin/reports/client-performance/at-risk', {
      params: { limit },
    });
    return response.data;
  },

  async exportClientPerformance(view, params = {}, clientId) {
    const response = await adminApi.get('/admin/reports/client-performance/export', {
      params: { ...params, view, ...(clientId ? { client_id: clientId } : {}) },
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `client-performance-${view}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return true;
  },

  async getClientPerformanceThresholds() {
    const response = await adminApi.get('/admin/settings/client-performance-thresholds');
    return response.data;
  },

  async updateClientPerformanceThresholds(payload) {
    const response = await adminApi.put('/admin/settings/client-performance-thresholds', payload);
    return response.data;
  },

  async getProductCosts(params = {}) {
    const response = await adminApi.get('/admin/product-costs', { params });
    return response.data;
  },

  async saveProductCosts(costs) {
    const response = await adminApi.put('/admin/product-costs', { costs });
    return response.data;
  },

  async rebuildSalesHistory() {
    const response = await adminApi.post('/admin/cron/sales-history-rebuild');
    return response.data;
  },

  async getRevenueReportLines(basis, params = {}) {
    const response = await adminApi.get(`/admin/reports/revenue/lines/${basis}`, { params });
    return response.data;
  },

  async batchUpdateOrderStatus(orderIds, status) {
    const response = await adminApi.post('/admin/orders/batch-status', { order_ids: orderIds, status });
    return response.data;
  },

  // Messages
  async sendMessage(messageData) {
    const response = await adminApi.post('/client-portal/admin/messages', messageData);
    return response.data;
  },

  async getMessageHistory() {
    const response = await adminApi.get('/admin/messages');
    return response.data;
  },

  // Invoice management (Phase 2)
  async getInvoices(status = null) {
    const params = status ? { status_filter: status } : {};
    const response = await adminApi.get('/invoices/admin', { params });
    return response.data;
  },

  async getInvoiceDetail(invoiceId) {
    const response = await adminApi.get(`/invoices/admin/${invoiceId}`);
    return response.data;
  },

  async approveAdjustment(invoiceId) {
    const response = await adminApi.post(`/invoices/admin/${invoiceId}/adjustment/approve`);
    return response.data;
  },

  async rejectAdjustment(invoiceId, rejectionReason) {
    const response = await adminApi.post(`/invoices/admin/${invoiceId}/adjustment/reject`, { rejection_reason: rejectionReason });
    return response.data;
  },

  // Manual Payments
  async applyManualPayment(invoiceId, paymentData) {
    const response = await adminApi.post(`/invoices/admin/${invoiceId}/manual-payment`, paymentData);
    return response.data;
  },

  async getInvoicePayments(invoiceId) {
    const response = await adminApi.get(`/invoices/admin/${invoiceId}/payments`);
    return response.data;
  },

  // Invoice Bulk Operations
  async getVoidCategories() {
    const response = await adminApi.get('/invoices/admin/void-categories');
    return response.data;
  },

  async cancelInvoice(invoiceId, cancelReason, notes = '') {
    const response = await adminApi.post(`/invoices/admin/${invoiceId}/cancel`, {
      cancel_reason: cancelReason,
      notes
    });
    return response.data;
  },

  async voidInvoice(invoiceId, voidData) {
    const response = await adminApi.post(`/invoices/admin/${invoiceId}/void`, voidData);
    return response.data;
  },

  async bulkCancelInvoices(invoiceIds, cancelReason, notes = '') {
    const response = await adminApi.post('/invoices/admin/bulk/cancel', {
      invoice_ids: invoiceIds,
      cancel_reason: cancelReason,
      notes
    });
    return response.data;
  },

  async bulkVoidInvoices(invoiceIds, voidCategory, voidReason, estimatedCost = null, notes = '') {
    const response = await adminApi.post('/invoices/admin/bulk/void', {
      invoice_ids: invoiceIds,
      void_category: voidCategory,
      void_reason: voidReason,
      estimated_production_cost: estimatedCost,
      notes
    });
    return response.data;
  },

  async bulkMarkPaid(invoiceIds, paymentType, comments = '') {
    const response = await adminApi.post('/invoices/admin/bulk/mark-paid', {
      invoice_ids: invoiceIds,
      payment_type: paymentType,
      comments
    });
    return response.data;
  },

  async bulkSendReminder(invoiceIds) {
    const response = await adminApi.post('/invoices/admin/bulk/send-reminder', {
      invoice_ids: invoiceIds
    });
    return response.data;
  },

  async updateInvoiceItems(invoiceId, items, adminNotes = '') {
    const response = await adminApi.put(`/invoices/admin/${invoiceId}/items`, {
      items,
      admin_notes: adminNotes
    });
    return response.data;
  },

  // Credit Memo Operations
  async getCreditMemos(filters = {}) {
    const response = await adminApi.get('/invoices/admin/credit-memos', { params: filters });
    return response.data;
  },

  async getCreditMemoSummary(clientId = null) {
    const params = clientId ? { client_id: clientId } : {};
    const response = await adminApi.get('/invoices/admin/credit-memos/summary', { params });
    return response.data;
  },

  async getCreditMemoDetail(creditMemoId) {
    const response = await adminApi.get(`/invoices/admin/credit-memos/${creditMemoId}`);
    return response.data;
  },

  async updateRefundStatus(creditMemoId, refundStatus, refundReference = null) {
    const response = await adminApi.patch(`/invoices/admin/credit-memos/${creditMemoId}/refund-status`, {
      refund_status: refundStatus,
      refund_reference: refundReference
    });
    return response.data;
  },

  // Refund Processing
  async getRefundMethods() {
    const response = await adminApi.get('/invoices/admin/refund-methods');
    return response.data;
  },

  async getCreditMemoPaymentInfo(creditMemoId) {
    const response = await adminApi.get(`/invoices/admin/credit-memos/${creditMemoId}/payment-info`);
    return response.data;
  },

  async processStripeRefund(creditMemoId, refundAmount = null, reason = null) {
    const response = await adminApi.post(`/invoices/admin/credit-memos/${creditMemoId}/refund/stripe`, {
      refund_amount: refundAmount,
      reason: reason
    });
    return response.data;
  },

  async processManualRefund(creditMemoId, refundMethod, refundReference = null, refundAmount = null, notes = null) {
    const response = await adminApi.post(`/invoices/admin/credit-memos/${creditMemoId}/refund/manual`, {
      refund_method: refundMethod,
      refund_reference: refundReference,
      refund_amount: refundAmount,
      notes: notes
    });
    return response.data;
  },

  // Payment warnings (Phase 4)
  async getPaymentWarnings() {
    const response = await adminApi.get('/payments/admin/warnings');
    return response.data;
  },

  async triggerAutoPay() {
    const response = await adminApi.post('/payments/auto-pay/process');
    return response.data;
  },

  async getAutoPayRetryQueue() {
    const response = await adminApi.get('/payments/auto-pay/retry-queue');
    return response.data;
  },

  async retryFailedPayment(invoiceId) {
    const response = await adminApi.post(`/payments/auto-pay/retry/${invoiceId}`);
    return response.data;
  },

  // Document Templates
  async getDocumentTemplates() {
    const response = await adminApi.get('/documents/templates');
    return response.data;
  },

  async getDocumentTemplate(id) {
    const response = await adminApi.get(`/documents/templates/${id}`);
    return response.data;
  },

  async createDocumentTemplate(data) {
    const response = await adminApi.post('/documents/templates', data);
    return response.data;
  },

  async updateDocumentTemplate(id, data) {
    const response = await adminApi.put(`/documents/templates/${id}`, data);
    return response.data;
  },

  async publishDocumentTemplate(templateId, clientIds, addendumSummary) {
    const response = await adminApi.post(`/documents/templates/${templateId}/publish`, {
      client_ids: clientIds,
      addendum_summary: addendumSummary,
    });
    return response.data;
  },

  async getClientDocuments(clientId) {
    const response = await adminApi.get(`/documents/clients/${clientId}`);
    return response.data;
  },

  async getClientDocumentsSummary(clientId) {
    const response = await adminApi.get(`/documents/clients/${clientId}/summary`);
    return response.data;
  },

  async deleteDocumentTemplate(id) {
    const response = await adminApi.delete(`/documents/templates/${id}`);
    return response.data;
  },

  async archiveDocumentTemplate(id) {
    const response = await adminApi.put(`/documents/templates/${id}/archive`);
    return response.data;
  },

  // -------- Kitchen Scheduling --------
  async getSchedulingSettings() {
    const response = await adminApi.get('/admin/scheduling/settings');
    return response.data;
  },

  async updateSchedulingSettings(payload) {
    const response = await adminApi.put('/admin/scheduling/settings', payload);
    return response.data;
  },

  async getSchedulingPositions() {
    const response = await adminApi.get('/admin/scheduling/positions');
    return response.data;
  },

  async getSchedulingStaff() {
    const response = await adminApi.get('/admin/scheduling/staff');
    return response.data;
  },

  async updateSchedulingStaff(userId, payload) {
    const response = await adminApi.put(`/admin/scheduling/staff/${userId}`, payload);
    return response.data;
  },

  async getKitchens(includeArchived = false) {
    const response = await adminApi.get('/admin/scheduling/kitchens', {
      params: { include_archived: includeArchived },
    });
    return response.data;
  },

  async createKitchen(payload) {
    const response = await adminApi.post('/admin/scheduling/kitchens', payload);
    return response.data;
  },

  async updateKitchen(kitchenId, payload) {
    const response = await adminApi.put(`/admin/scheduling/kitchens/${kitchenId}`, payload);
    return response.data;
  },

  async archiveKitchen(kitchenId) {
    const response = await adminApi.delete(`/admin/scheduling/kitchens/${kitchenId}`);
    return response.data;
  },

  async getKitchenAvailability(kitchenId) {
    const response = await adminApi.get(`/admin/scheduling/kitchens/${kitchenId}/availability`);
    return response.data;
  },

  async createAvailabilityVersion(kitchenId, payload) {
    const response = await adminApi.post(`/admin/scheduling/kitchens/${kitchenId}/availability`, payload);
    return response.data;
  },

  async updateAvailabilityVersion(versionId, payload) {
    const response = await adminApi.put(`/admin/scheduling/availability/${versionId}`, payload);
    return response.data;
  },

  async deleteAvailabilityVersion(versionId) {
    const response = await adminApi.delete(`/admin/scheduling/availability/${versionId}`);
    return response.data;
  },

  async previewAvailabilityImpact(payload) {
    const response = await adminApi.post('/admin/scheduling/availability/impact-preview', payload);
    return response.data;
  },

  async getKitchenOverrides(kitchenId, start, end) {
    const response = await adminApi.get(`/admin/scheduling/kitchens/${kitchenId}/overrides`, {
      params: { start, end },
    });
    return response.data;
  },

  async saveKitchenOverride(kitchenId, payload) {
    const response = await adminApi.post(`/admin/scheduling/kitchens/${kitchenId}/overrides`, payload);
    return response.data;
  },

  async deleteKitchenOverride(overrideId) {
    const response = await adminApi.delete(`/admin/scheduling/overrides/${overrideId}`);
    return response.data;
  },

  async getKitchenBookings(start, end, kitchenId) {
    const response = await adminApi.get('/admin/scheduling/bookings', {
      params: { start, end, kitchen_id: kitchenId },
    });
    return response.data;
  },

  async createKitchenBooking(payload) {
    const response = await adminApi.post('/admin/scheduling/bookings', payload);
    return response.data;
  },

  async updateKitchenBooking(bookingId, payload) {
    const response = await adminApi.put(`/admin/scheduling/bookings/${bookingId}`, payload);
    return response.data;
  },

  async deleteKitchenBooking(bookingId) {
    const response = await adminApi.delete(`/admin/scheduling/bookings/${bookingId}`);
    return response.data;
  },

  async previewBookingImpact(payload) {
    const response = await adminApi.post('/admin/scheduling/bookings/impact-preview', payload);
    return response.data;
  },

  async getScheduleWeek(weekStart) {
    const response = await adminApi.get('/admin/scheduling/week', { params: { week_start: weekStart } });
    return response.data;
  },

  async createShift(payload) {
    const response = await adminApi.post('/admin/scheduling/shifts', payload);
    return response.data;
  },

  async updateShift(shiftId, payload) {
    const response = await adminApi.put(`/admin/scheduling/shifts/${shiftId}`, payload);
    return response.data;
  },

  async deleteShift(shiftId) {
    const response = await adminApi.delete(`/admin/scheduling/shifts/${shiftId}`);
    return response.data;
  },

  async publishScheduleWeek(weekStart) {
    const response = await adminApi.post(`/admin/scheduling/weeks/${weekStart}/publish`);
    return response.data;
  },

  async copyScheduleWeek(sourceWeek, targetWeek, options = {}) {
    const response = await adminApi.post('/admin/scheduling/weeks/copy', {
      source_week: sourceWeek,
      target_week: targetWeek,
      include_hours: options.includeHours !== false,
      include_shifts: options.includeShifts !== false,
    });
    return response.data;
  },

  async getHoursPattern(kitchenId) {
    const response = await adminApi.get(`/admin/scheduling/kitchens/${kitchenId}/hours-pattern`);
    return response.data;
  },

  async saveHoursPattern(kitchenId, payload) {
    const response = await adminApi.put(`/admin/scheduling/kitchens/${kitchenId}/hours-pattern`, payload);
    return response.data;
  },

  async setHoursPatternStatus(kitchenId, status) {
    const response = await adminApi.post(
      `/admin/scheduling/kitchens/${kitchenId}/hours-pattern/status`, { status },
    );
    return response.data;
  },

  async previewHoursPattern(kitchenId) {
    const response = await adminApi.post(`/admin/scheduling/kitchens/${kitchenId}/hours-pattern/preview`, {});
    return response.data;
  },

  async applyHoursPattern(kitchenId) {
    const response = await adminApi.post(`/admin/scheduling/kitchens/${kitchenId}/hours-pattern/apply`, {});
    return response.data;
  },

  async hoursPatternForDay(kitchenId, date, commit = false) {
    const response = await adminApi.post(
      `/admin/scheduling/kitchens/${kitchenId}/hours-pattern/day`, { date, commit },
    );
    return response.data;
  },

  async getHoursSuggestions(kitchenId) {
    const response = await adminApi.get(`/admin/scheduling/kitchens/${kitchenId}/hours-suggestions`);
    return response.data;
  },

  async applyHoursSuggestion(kitchenId, date, force = false) {
    const response = await adminApi.post(
      `/admin/scheduling/kitchens/${kitchenId}/hours-suggestions/apply`, { date, force },
    );
    return response.data;
  },

  async dismissHoursSuggestion(kitchenId, date) {
    const response = await adminApi.post(
      `/admin/scheduling/kitchens/${kitchenId}/hours-suggestions/dismiss`, { date },
    );
    return response.data;
  },

  async serveNextDayBookings(kitchenId, dates = null) {
    const response = await adminApi.post(
      `/admin/scheduling/kitchens/${kitchenId}/bookings/serve-next-day`,
      dates ? { dates } : {},
    );
    return response.data;
  },

  async copyHoursIntoWeeks(sourceWeek, weeks, commit = false) {
    const response = await adminApi.post('/admin/scheduling/hours/copy-weeks', {
      source_week: sourceWeek, weeks, commit,
    });
    return response.data;
  },

  async getCoverageBoard(start, end) {
    const response = await adminApi.get('/admin/scheduling/coverage', { params: { start, end } });
    return response.data;
  },

  async evaluateCoverage(payload = {}) {
    const response = await adminApi.post('/admin/scheduling/coverage/evaluate', payload);
    return response.data;
  },

  async getCoverageIncidents(params = {}) {
    const response = await adminApi.get('/admin/scheduling/incidents', { params });
    return response.data;
  },

  async getShiftRequests(status = 'open') {
    const response = await adminApi.get('/admin/scheduling/requests', { params: { status } });
    return response.data;
  },

  async decideShiftRequest(requestId, approve, note = '') {
    const response = await adminApi.post(
      `/admin/scheduling/requests/${requestId}/decide`, { approve, note },
    );
    return response.data;
  },

  async revokeTimeOff(requestId, note = '') {
    const response = await adminApi.post(
      `/admin/scheduling/requests/${requestId}/revoke-time-off`, { note },
    );
    return response.data;
  },

  async getApprovedTimeOff(start, end) {
    const response = await adminApi.get('/admin/scheduling/time-off', { params: { start, end } });
    return response.data;
  },

  async getSuggestedLabourStandards() {
    const response = await adminApi.get('/admin/scheduling/labour-standards/suggested');
    return response.data;
  },
};

export default adminApi;
